import { color, FlexBox, Text } from "@smoothincorg/smooth-ui"
import { AxiosResponse } from "axios"
import dayjs from "dayjs"
import Link from "next/link"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { HiExternalLink } from "react-icons/hi"
import { A } from "src/components/A"
import { BorderedButton } from "src/components/Button"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import { RESTRICTED_ADMIN_URL } from "src/lib/constants/value"

interface AutoExaminationPartProps {
  autoExam: any
  mes_id: number
  autoExaminationLabel: string | null
  updateKey: number
  setUpdateKey: (key: number) => void
  mesLabel: string
  interestAddOn: number
}

export const AutoExaminationPart = ({
  autoExam,
  mes_id,
  autoExaminationLabel,
  updateKey,
  setUpdateKey,
  mesLabel,
  interestAddOn,
}: AutoExaminationPartProps) => {
  const router = useRouter()
  const [isDebugMode, setIsDebugMode] = useState(false)
  const { postWithToken, patchWithToken, deleteWithToken } = useSmoothApi()
  const [hasLabel, setHasLabel] = useState(autoExaminationLabel !== null)

  useEffect(() => {
    if (router.isReady) {
      setIsDebugMode(router.query.debug === "t")
    }
  }, [router.isReady])

  const shouldHideButtons = (requestType: string) => {
    return (
      requestType === "re_credit_model" ||
      requestType === "cic_delete" ||
      requestType === "cic_correct"
    )
  }

  const requestTypeTranslations: { [key in string]: string } = {
    ["auto_examine"]: "新規照会",
    ["re_examine"]: "新規照会(後続のAI与信は行わない)",
    ["re_credit_model"]: "AI与信審査(自動決裁あり)",
    ["re_credit_model_no_auto_decision"]: "AI与信審査(自動決裁なし)",
    ["cic_delete"]: "CIC削除",
    ["cic_correct"]: "CIC訂正",
    ["cic_refer_back"]: "再照会",
    ["cic_similar"]: "類似照会",
  }

  type ApiCallFunction = (...args: any[]) => Promise<AxiosResponse>

  const handleResult = (axiosFunc: ApiCallFunction, title: string) => {
    const confirmResult = window.confirm(`${title} リクエストを送信しますか？`)

    if (confirmResult) {
      axiosFunc()
        .then((res) => {
          window.alert(`${title} リクエスト成功 \n ${JSON.stringify(res.data)}`)
          console.log(res.data)
          setUpdateKey(updateKey + 1)
        })
        .catch((err) => {
          window.alert(`${title} リクエスト失敗 \n ${JSON.stringify(err.response.data)}`)
          console.error(err)
        })
    }
  }

  const handleReRunCreditModelButtonClick = (mes_id: number) => {
    handleResult(
      () =>
        postWithToken(
          SMOOTH_SMOOTHER_API_URL +
            `/auto_examinations/examinations/${mes_id}/examination_requests/rerun_credit_model?check_auto_decision=true`
        ),
      "AI与信審査(自動決裁あり)"
    )
  }

  const handleReRunCreditModelNoDecisionButtonClick = (mes_id: number) => {
    handleResult(
      () =>
        postWithToken(
          SMOOTH_SMOOTHER_API_URL +
            `/auto_examinations/examinations/${mes_id}/examination_requests/rerun_credit_model?check_auto_decision=false`
        ),
      "AI与信審査(自動決裁なし)"
    )
  }

  const handleNewAutoExamReqButtonClick = (mes_id: number) => {
    handleResult(
      () =>
        postWithToken(
          SMOOTH_SMOOTHER_API_URL + `/auto_examinations/examinations/${mes_id}/examination_requests`
        ),
      "新規照会"
    )
  }

  const handleNewReferBackAutoExamReqButtonClick = (mes_id: number) => {
    handleResult(
      () =>
        postWithToken(
          SMOOTH_SMOOTHER_API_URL +
            `/auto_examinations/examinations/${mes_id}/examination_requests/refer_back`
        ),
      "新規再照会"
    )
  }

  const handleAutoExamReqCorrectionButtonClick = (mes_id: number, request_label: string) => {
    handleResult(
      () =>
        patchWithToken(
          SMOOTH_SMOOTHER_API_URL +
            `/auto_examinations/examinations/${mes_id}/examination_requests/${request_label}`
        ),
      `ExaminationRequest ${request_label} 訂正`
    )
  }

  const handleAutoExamReqDestroyButtonClick = async (mes_id: number, request_label: string) => {
    handleResult(
      () =>
        deleteWithToken(
          SMOOTH_SMOOTHER_API_URL +
            `/auto_examinations/examinations/${mes_id}/examination_requests/${request_label}`
        ),
      `ExaminationRequest ${request_label} 削除`
    )
  }

  const handleSyncWithAutoDecisionButtonClick = async (mes_id: number) => {
    handleResult(() => {
      return postWithToken(
        SMOOTH_SMOOTHER_API_URL +
          `/auto_examinations/examinations/${mes_id}/examination_requests/sync?auto_decisionable=true`
      )
    }, `連携&照会&AI与信審査(自動決裁あり)`)
  }

  const handleSyncWithoutAutoDecisionButtonClick = async (mes_id: number) => {
    handleResult(() => {
      return postWithToken(
        SMOOTH_SMOOTHER_API_URL +
          `/auto_examinations/examinations/${mes_id}/examination_requests/sync?auto_decisionable=false`
      )
    }, `連携&照会&AI与信審査(自動決裁なし)`)
  }

  const latestCreditModelResult = (): any => {
    if (autoExam && autoExam.examination) {
      autoExam.examination.requests.sort((a: any, b: any) => {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      })

      const latestRequestWithCreditModel = autoExam.examination.requests.find(
        (req: any) => req.creditModelResult
      )

      if (latestRequestWithCreditModel) {
        return latestRequestWithCreditModel.creditModelResult
      }
    }

    return null
  }

  const isPaymentCapacityInsufficient = () => {
    if (autoExam?.examination?.paymentPossibleAmount) {
      return (Number(autoExam?.examination?.paymentPossibleAmount) * 1000) / interestAddOn < 70000
    }
    return false
  }

  const buttonStyle = {
    padding: "1rem",
    margin: "0.5rem",
    backgroundColor: color.main,
  }

  return (
    <FlexBox className={`main-examination-score__row`} flexDirection="column">
      <FlexBox alignItems="center">
        <FlexBox justifyContent="space-between" alignItems="center">
          <Text size={14} weight="bold">
            AI与信審査{" "}
          </Text>
        </FlexBox>
        <FlexBox flexDirection="column" alignItems="flex-start">
          <FlexBox flexDirection="row" justifyContent="flex-start" alignItems="center">
            <Text>結果:{latestCreditModelResult()?.credit_result}</Text>&nbsp;&nbsp;
            <Text>スコア:{latestCreditModelResult()?.credit_score}</Text>&nbsp;&nbsp;
            <Text>与信額:{latestCreditModelResult()?.credit_amount}</Text>
          </FlexBox>
          <FlexBox flexDirection="row" justifyContent="flex-start" alignItems="center">
            <Text>支払可能見込額:{autoExam?.examination?.paymentPossibleAmount}千円</Text>&nbsp;
            {isPaymentCapacityInsufficient() && <Text color="red">&nbsp;シカチョウアウト</Text>}
          </FlexBox>
        </FlexBox>
        <FlexBox justifyContent="flex-start" alignItems="center">
          {hasLabel && (
            <>
              <BorderedButton
                style={buttonStyle}
                onClick={() => handleReRunCreditModelButtonClick(mes_id)}
              >
                <Text color="white">AI与信審査(自動決裁あり)</Text>
              </BorderedButton>
              <BorderedButton
                style={buttonStyle}
                onClick={() => handleReRunCreditModelNoDecisionButtonClick(mes_id)}
              >
                <Text color="white">AI与信審査(自動決裁なし)</Text>
              </BorderedButton>
              <BorderedButton
                style={buttonStyle}
                onClick={() => handleNewAutoExamReqButtonClick(mes_id)}
              >
                <Text color="white">新規照会</Text>
              </BorderedButton>
              <BorderedButton
                style={buttonStyle}
                onClick={() => handleNewReferBackAutoExamReqButtonClick(mes_id)}
              >
                <Text color="white">新規再照会</Text>
              </BorderedButton>
            </>
          )}
          {!hasLabel && (
            <>
              <BorderedButton
                style={buttonStyle}
                onClick={async () => {
                  await handleSyncWithAutoDecisionButtonClick(mes_id)
                  setHasLabel(true)
                }}
              >
                <Text color="white">連携&照会&AI与信審査(自動決裁あり)</Text>
              </BorderedButton>
              <BorderedButton
                style={buttonStyle}
                onClick={async () => {
                  await handleSyncWithoutAutoDecisionButtonClick(mes_id)
                  setHasLabel(true)
                }}
              >
                <Text color="white">連携&照会&AI与信審査(自動決裁なし)</Text>
              </BorderedButton>
              <BorderedButton
                style={buttonStyle}
                onClick={async () => {
                  await handleNewReferBackAutoExamReqButtonClick(mes_id)
                  setHasLabel(true)
                }}
              >
                <Text color="white">新規再照会</Text>
              </BorderedButton>
            </>
          )}
        </FlexBox>
        <FlexBox flexDirection="column">
          <FlexBox justifyContent="flex-start" alignItems="center">
            <A
              href={`${RESTRICTED_ADMIN_URL}/examinations/${mesLabel}`}
              target="_blank"
              rel="noreferrer"
            >
              <Text size={14} color="main">
                Restricted個別審査画面
                <HiExternalLink />
              </Text>
            </A>
          </FlexBox>
        </FlexBox>
      </FlexBox>
      <table className="table-auto items-center justify-center">
        <thead className="border-b font-bold">
          <tr className="h-16 rounded-sm border border-gray-100">
            <th className="px-6 py-4">照会区分</th>
            <th className="px-6 py-4">ステータス</th>
            <th className="px-6 py-4">日時</th>
            <th className="px-6 py-4">JSON</th>
            <th className="px-6 py-4">操作</th>
          </tr>
        </thead>
        <tbody
          style={{
            textAlign: "center",
          }}
        >
          {autoExam &&
            autoExam.examination &&
            autoExam.examination.requests.map((exam_req: any) => (
              <tr key={exam_req.label} className="h-16 border-b border-gray-100">
                <td className="px-6 py-4">
                  {requestTypeTranslations[exam_req.requestType] || exam_req.requestType}(
                  {exam_req.requestType})
                </td>
                <td className="px-6 py-4">{exam_req.progress}</td>
                <td className="px-6 py-4">
                  {dayjs(exam_req.createdAt).format("YYYY-MM-DD HH:mm")}
                </td>
                <td className="px-6 py-4">
                  {exam_req.cicResponseDownloadUrl && (
                    <Link href={exam_req.cicResponseDownloadUrl} target="_blank">
                      JSON DL
                    </Link>
                  )}
                </td>
                <td className="px-6 py-4">
                  {!shouldHideButtons(exam_req.requestType) && (
                    <div>
                      <BorderedButton
                        style={{
                          margin: "0.5rem",
                          backgroundColor: "#F1D263",
                        }}
                        onClick={() =>
                          handleAutoExamReqCorrectionButtonClick(mes_id, exam_req.label)
                        }
                      >
                        <Text color="white">訂正</Text>
                      </BorderedButton>
                      <BorderedButton
                        style={{
                          backgroundColor: "#F1D263",
                        }}
                        onClick={() => handleAutoExamReqDestroyButtonClick(mes_id, exam_req.label)}
                      >
                        <Text color="white">削除</Text>
                      </BorderedButton>
                    </div>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {isDebugMode && (
        <FlexBox>
          <code
            style={{
              whiteSpace: "pre",
            }}
          >
            {JSON.stringify(autoExam)}
          </code>
        </FlexBox>
      )}
    </FlexBox>
  )
}
